import {
	Accordion,
	AccordionDetails,
	AccordionSummary,
	Box,
	Collapse,
	IconButton,
	Typography,
} from '@mui/material';
import { GridExpandMoreIcon } from '@mui/x-data-grid';
import CustomTooltip from '@src/components/CustomTootip/CustomTooltip';
import ImageThumnail from '@src/components/ImagePreview/ImageThumnail';
import { generateUniqueID } from '@src/helpers/Helpers';
import { Image } from '@src/interfaces/type';
import { useAppDispatch } from '@src/store/hooks';
import { setPreviewImages } from '@src/store/reducers/UIReducer';
import React, { useState } from 'react';
import styled from 'styled-components';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import {
	FulfillmentStatus,
	ReadyToShipStatus,
	ShippedStatus,
} from '@src/components/Main/Chat/ChatMessage/ChatMessage';
import moment from 'moment';

const StyledContainer = styled.div`
	display: flex;
	flex-direction: column;
	letter-spacing: 0.28px;
	width: 100%;
	padding: 16px;
	gap: 16px;
	#fields {
		display: flex;
		flex-wrap: wrap;
		gap: 14px;
	}
`;

const StyledBox = styled.div`
	display: flex;
	flex-direction: column;
	width: 100%;
	border-radius: 6px;
	border-left: 1.5px solid var(--Gray-g-10, #f1f1f1);
	border-right: 1.5px solid var(--Gray-g-10, #f1f1f1);
	border-bottom: 1.5px solid var(--Gray-g-10, #f1f1f1);
	background: var(--others-white, #fff);
	#heading {
		display: flex;
		justify-content: space-between;
		align-items: center;
		border-radius: 6px;
		border: 1.5px solid var(--Gray-g-10, #f1f1f1);
		background: var(--Gray-g-0, #fdfdfd);
		width: 100%;
		padding-left: 6px;
		cursor: pointer;
		p {
			color: var(--Gray-g-700, #5c5c5c);
			font-family: Poppins;
			font-size: 14px;
			font-weight: 500;
			line-height: 23.4px;
		}
	}
`;

const StyledLabel = styled(Box)`
	color: var(--color-font-grey);
	font-size: 14px;
	font-weight: 600;
	line-height: 15.6px;
`;

const FieldConatiner = styled.div({
	display: 'flex',
	alignItems: 'center',
	gap: '10px',
	'#key': {
		fontSize: '14px',
		fontWeight: 400,
		color: '#979797',
	},
	'#value-container': {
		display: 'flex',
		alignItems: 'center',
		paddingLeft: '2px',
		minWidth: 'auto',
		maxWidth: '150px',
		whiteSpace: 'nowrap',
		overflow: 'hidden',
		textOverflow: 'ellipsis',
		fontSize: '14px',
		fontWeight: 600,
		color: '#7D7D7D',
	},
});

interface FieldProp {
	fieldKey: string;
	value: string;
	valueStyle?: any;
	keyStyle?: any;
}

const Field: React.FC<FieldProp> = ({ fieldKey, value, valueStyle = {}, keyStyle = {} }) => {
	return (
		<FieldConatiner>
			{!!fieldKey ? (
				<div id="key" style={keyStyle}>
					{fieldKey}
				</div>
			) : null}
			<CustomTooltip title={value} placement="top">
				<div id="value-container" style={valueStyle}>
					{value}
				</div>
			</CustomTooltip>
		</FieldConatiner>
	);
};

interface Props {
	data: any;
	title: 'Sales Data' | 'Invoice' | 'Payment Attachment';
}

const StatusDetails: React.FC<Props> = ({ data, title }) => {
	const [collapse, setCollapse] = useState(false);
	const [files, setFiles] = useState<File[]>([]);
	const [lrImages, setLrImages] = useState<Image[]>(
		data?.attachments?.lrCopy
			? [{ id: generateUniqueID(), url: data?.attachments?.lrCopy, hover: false }]
			: []
	);
	const [images, setImages] = useState<Image[]>(
		data?.image ? [{ id: generateUniqueID(), url: data?.image, hover: false }] : []
	);
	const [eWayBillimages, setEwayBillImages] = useState<Image[]>(
		data?.attachments?.eWayBill
			? [{ id: generateUniqueID(), url: data?.attachments?.eWayBill, hover: false }]
			: []
	);
	const [paymentImages, setPaymentImages] = useState<Image[]>(
		data?.attachments?.paymentAttachment
			? [{ id: generateUniqueID(), url: data?.attachments?.paymentAttachment, hover: false }]
			: []
	);

	const dispatch = useAppDispatch();
	const openPreviewLrImage = () => {
		dispatch(setPreviewImages([lrImages?.[0]?.url]));
	};
	const openPreviewEwayImage = () => {
		dispatch(setPreviewImages([eWayBillimages?.[0]?.url]));
	};
	const openPreviewPyament = () => {
		dispatch(setPreviewImages([paymentImages?.[0]?.url]));
	};

	const salesData = [
		{ key: 'Sales Order', value: data?.salesOrderNumber || 'N/A' },
		{ key: 'Packing Instructions', value: data?.packingInstructions || 'N/A' },
		{ key: 'Logistic Instructions', value: data?.logisticInstructions || 'N/A' },
		{ key: 'Remarks', value: data?.remarks || 'N/A' },
		{ key: 'Customer PO#', value: data?.customerPoNumber || 'N/A' },
		{ key: 'SO Date', value: data?.soDate ? moment(data?.soDate).format('DD-MMM-YYYY') : 'N/A' },
		{ key: 'Payment Terms', value: data?.paymentTerms?.name || 'N/A' },
	];

	const invoiceData = [{ key: 'Invoice', value: data?.invoiceNumber || 'N/A' }];

	const paymentAttachment = [
		{ key: 'Payment Mode', value: data?.paymentMode?.name || 'N/A' },
		{ key: 'Received Amount', value: data?.receivedAmount || 'N/A' },
		{ key: 'Payment Status', value: data?.paymentStatus?.name || 'N/A' },
	];

	if (title === 'Sales Data') {
		return (
			<StyledBox onClick={() => setCollapse(!collapse)}>
				<div id="heading">
					<p>Sales Data</p>
					<IconButton onClick={() => setCollapse(!collapse)}>
						{collapse ? <ExpandLessIcon /> : <GridExpandMoreIcon />}
					</IconButton>
				</div>
				<Collapse in={collapse}>
					<StyledContainer>
						<div id="fields">
							{salesData.map((field, index) => (
								<Field
									key={index}
									fieldKey={`${field.key}:`}
									value={field.value}
									valueStyle={{ maxWidth: '250px', minWidth: 'auto' }}
								/>
							))}
						</div>
					</StyledContainer>
				</Collapse>
			</StyledBox>
		);
	} else if (title === 'Invoice') {
		return (
			<StyledBox onClick={() => setCollapse(!collapse)}>
				<div id="heading">
					<p>Invoice, LR Copy, E-wayBill</p>
					<IconButton onClick={() => setCollapse(!collapse)}>
						{collapse ? <ExpandLessIcon /> : <GridExpandMoreIcon />}
					</IconButton>
				</div>
				<Collapse in={collapse}>
					<StyledContainer>
						<div id="fields">
							{invoiceData?.map((field, index) => (
								<Field
									key={index}
									fieldKey={`${field.key}:`}
									value={field.value}
									valueStyle={{ maxWidth: '250px', minWidth: 'auto' }}
								/>
							))}
						</div>
						{lrImages?.length ? (
							<div style={{ display: 'flex', flexDirection: 'column', gap: '10px', width: '100%' }}>
								<StyledLabel>LR Copy</StyledLabel>
								<ImageThumnail
									images={lrImages}
									setImages={setLrImages}
									setFiles={setFiles}
									style={{}}
									handleFileChange={() => {}}
									openPreview={openPreviewLrImage}
									readOnly
								/>
							</div>
						) : null}
						{eWayBillimages?.length ? (
							<div style={{ display: 'flex', flexDirection: 'column', gap: '10px', width: '100%' }}>
								<StyledLabel>E-way Bill</StyledLabel>
								<ImageThumnail
									images={eWayBillimages}
									setImages={setEwayBillImages}
									setFiles={setFiles}
									style={{}}
									handleFileChange={() => {}}
									openPreview={openPreviewEwayImage}
									readOnly
								/>
							</div>
						) : null}
					</StyledContainer>
				</Collapse>
			</StyledBox>
		);
	} else {
		return (
			<StyledBox onClick={() => setCollapse(!collapse)}>
				<div id="heading">
					<p>Payment Attachment</p>
					<IconButton onClick={() => setCollapse(!collapse)}>
						{collapse ? <ExpandLessIcon /> : <GridExpandMoreIcon />}
					</IconButton>
				</div>
				<Collapse in={collapse}>
					<StyledContainer>
						<div id="fields">
							{paymentAttachment?.map((field, index) => (
								<Field
									key={index}
									fieldKey={`${field.key}:`}
									value={field.value}
									valueStyle={{ maxWidth: '250px', minWidth: 'auto' }}
								/>
							))}
						</div>
						{paymentImages?.length ? (
							<div style={{ display: 'flex', flexDirection: 'column', gap: '10px', width: '100%' }}>
								<ImageThumnail
									images={paymentImages}
									setImages={setPaymentImages}
									setFiles={setFiles}
									style={{}}
									handleFileChange={() => {}}
									openPreview={openPreviewPyament}
									readOnly
								/>
							</div>
						) : null}
					</StyledContainer>
				</Collapse>
			</StyledBox>
		);
	}
	return null;
};

export default StatusDetails;
